import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import type { Project } from "../lib/types";
import { ConfirmDialog } from "./ConfirmDialog";

/**
 * Deleting a project, wherever it is triggered from — the list or the project's
 * own page.
 *
 * Owner-only, and genuinely destructive: the orders, payments, commission, the
 * subscription and the licence go with it. So the dialog names the count of
 * everything that will be destroyed before the reference is typed, rather than
 * describing the act in the abstract. What the operator confirms is the actual
 * damage, not the idea of it.
 */
export function DeleteProject({
  project,
  open,
  onCancel,
  onDeleted,
}: {
  project: Project;
  open: boolean;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: (confirm: string) =>
      api(`/api/v1/admin/projects/${project.id}`, { method: "DELETE", body: { confirm } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      onDeleted();
    },
  });

  const casualties = [
    count(project._count.orders, "order", "orders"),
    project.subscription === null ? null : "the subscription",
    project.license === null ? null : `the licence ••••${project.license.keyLast4}`,
  ].filter((item): item is string => item !== null);

  return (
    <ConfirmDialog
      open={open}
      title="Delete this project"
      confirmPhrase={project.reference}
      confirmLabel="Delete project"
      pending={remove.isPending}
      error={remove.isError ? messageFor(remove.error) : null}
      onCancel={onCancel}
      onConfirm={(typed) => remove.mutate(typed)}
      description={
        <>
          <p>
            <strong className="text-ink">{project.name}</strong> and everything attached to it
            will be destroyed. This cannot be undone.
          </p>
          {casualties.length > 0 && (
            <p>
              That includes {joinList(casualties)}. Payments and commission go with the orders —
              a payment against an order that no longer exists is not history.
            </p>
          )}
          <p>
            If the work is simply finished, set the status to <strong>Ended</strong> instead. That
            closes the project and keeps every record.
          </p>
        </>
      }
    />
  );
}

function count(n: number, one: string, many: string): string | null {
  if (n === 0) return null;
  return `${n} ${n === 1 ? one : many}`;
}

/** Serial comma omitted deliberately: these lists are two or three items long. */
function joinList(items: string[]): string {
  if (items.length === 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "Could not delete this project.";
}
